import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Download, Menu, X } from 'lucide-react';
import { container, EASE_OUT_EXPO } from './editorial';
import { SITE } from '../lib/site';
import { cn } from '../lib/utils';

const NAV_ITEMS = [
  { id: 'ask', label: 'Ask me' },
  { id: 'experience', label: 'Experience' },
  { id: 'projects', label: 'Projects' },
  { id: 'skills', label: 'Skills' },
  { id: 'recommendations', label: 'Kind words' },
  { id: 'contact', label: 'Say hi' },
];

// Tracks which page section sits in the middle band of the viewport.
function useActiveSection() {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );
    const watched = new Set<Element>();
    const watchSections = () =>
      document.querySelectorAll('main section[id]').forEach((section) => {
        if (watched.has(section)) return;
        watched.add(section);
        observer.observe(section);
      });
    watchSections();
    // Most sections arrive a moment later in their own chunk.
    const main = document.getElementById('main');
    const mutations = new MutationObserver(watchSections);
    if (main) mutations.observe(main, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, []);

  return active;
}

export default function Navigation() {
  const active = useActiveSection();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Close the sheet first, then scroll once body scrolling is unlocked.
  const goToFromMenu = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    setOpen(false);
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView();
      window.history.replaceState(null, '', `#${id}`);
    });
  };

  return (
    <>
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-300',
          scrolled || open ? 'border-edge bg-night/70 backdrop-blur-xl' : 'border-transparent bg-transparent'
        )}
      >
        <div className={cn(container, 'flex h-16 items-center justify-between gap-6')}>
          <a href="#hero" className="flex items-center gap-2.5 text-[17px] font-semibold tracking-[-0.02em] text-fg">
            <span className="grid h-6 w-6 place-items-center rounded-[7px] bg-azure font-mono text-xs font-medium text-night">D</span>
            Danial Darabi
          </a>

          <nav aria-label="Primary" className="hidden lg:block">
            <ul className="glass flex items-center gap-0.5 rounded-full border border-edge p-1">
              {NAV_ITEMS.map((item) => {
                const isActive = active === item.id;
                return (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      aria-current={isActive ? 'true' : undefined}
                      className={cn(
                        'relative inline-flex h-9 items-center rounded-full px-3.5 text-[13px] transition-colors duration-200',
                        isActive ? 'text-fg' : 'text-fg-muted hover:text-fg'
                      )}
                    >
                      {isActive && (
                        <motion.span
                          layoutId="nav-active"
                          className="absolute inset-0 rounded-full bg-night-3"
                          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                        />
                      )}
                      <span className="relative">{item.label}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={SITE.resume}
              target="_blank"
              rel="noopener noreferrer"
              className="glass hidden h-10 items-center gap-2 rounded-full border border-edge-strong px-4 text-sm text-fg transition-colors duration-200 hover:border-azure/60 sm:inline-flex"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              CV
            </a>
            <button
              ref={toggleRef}
              type="button"
              onClick={() => setOpen((value) => !value)}
              aria-expanded={open}
              aria-controls="mobile-menu"
              aria-label={open ? 'Close menu' : 'Open menu'}
              className="glass inline-flex h-11 w-11 items-center justify-center rounded-full border border-edge-strong text-fg transition-colors hover:border-azure/60 lg:hidden"
            >
              {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </header>

      {/* Kept outside <header>: its backdrop-filter would become the containing
          block for this fixed sheet and collapse it to zero height. */}
      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-menu"
            aria-label="Mobile"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 bottom-0 top-16 z-40 overflow-y-auto bg-night lg:hidden"
          >
            <ul className={cn(container, 'flex flex-col pt-6')}>
              {NAV_ITEMS.map((item, index) => (
                <motion.li
                  key={item.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.04 * index, duration: 0.4, ease: EASE_OUT_EXPO }}
                >
                  <a
                    href={`#${item.id}`}
                    onClick={(event) => goToFromMenu(event, item.id)}
                    className="flex items-baseline justify-between border-b border-edge py-4 text-3xl font-semibold tracking-[-0.03em] text-fg transition-colors hover:text-azure-text"
                  >
                    {item.label}
                    <span className="font-mono text-xs font-normal text-fg-subtle">{String(index + 1).padStart(2, '0')}</span>
                  </a>
                </motion.li>
              ))}
            </ul>
            <div className={cn(container, 'py-8')}>
              <a
                href={SITE.resume}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-azure font-medium text-night"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Download CV
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
}
